import { FC, Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactQuill, { type EmitterSource } from 'react-quill-new';
import { cn } from '@/shared/lib';
import { normalizeQuillHtml, RICH_TEXT_FORMATS } from './quillHtml';
import { RichTextEditorToolbar } from './RichTextEditorToolbar';
import { RichTextEditorHandle, RichTextFormats, RichTextListType } from './types';
import './quill.css';

/**
 * Тема `default` — оформление Quill без готового тулбара: у нас свой, на компонентах проекта.
 * Обёртка по умолчанию подставляет `snow`, поэтому значение задаётся явно.
 */
const EDITOR_THEME = 'default';

/** Ссылки на объекты должны быть стабильными: обёртка пересоздаёт редактор при их изменении */
const EDITOR_MODULES = { toolbar: false };

type Props = {
  /** HTML содержимого редактора */
  value: string;
  /** Вызывается с нормализованным HTML при каждой правке */
  onChange: (html: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  autoFocus?: boolean;
  /** Классы внешней обёртки (тулбар + поле) */
  className?: string;
  /** Классы поля ввода — сюда задаётся высота, например `max-h-48` */
  editorClassName?: string;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  ref?: Ref<RichTextEditorHandle>;
};

/**
 * Редактор форматированного текста на Quill: принимает и отдаёт HTML.
 *
 * Тулбар собран из компонентов проекта, поле стилизовано под остальные инпуты.
 * Набор форматов ограничен `RICH_TEXT_FORMATS` — тем, что переживает санитизацию при просмотре.
 *
 * Высота не задана: ограничивать её нужно через `editorClassName` (`max-h-*`), иначе поле
 * растёт по содержимому и способно вытолкнуть за экран контейнер вроде `Popover`.
 */
export const RichTextEditor: FC<Props> = ({
  value,
  onChange,
  placeholder,
  readOnly,
  autoFocus,
  className,
  editorClassName,
  onKeyDown,
  ref,
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const [formats, setFormats] = useState<RichTextFormats>({});

  /**
   * Содержимое в том виде, в каком его отдаёт Quill.
   *
   * Наружу уходит нормализованный HTML, но внутрь возвращать его нельзя: Quill хранит списки
   * и пробелы по-своему, а обёртка считает поле устаревшим, пока значение не совпадает с её
   * собственным, — и переписывает содержимое на каждый повторный рендер, теряя набранное.
   */
  const [editorValue, setEditorValue] = useState(value);
  /** Актуальное содержимое для эффектов: state в их замыкание попадает с опозданием */
  const editorValueRef = useRef(value);
  /** Последний HTML, отданный наружу — по нему отличаем эхо от внешнего изменения value */
  const lastEmittedHtmlRef = useRef(value);

  const applyEditorValue = useCallback((html: string) => {
    editorValueRef.current = html;
    setEditorValue(html);
  }, []);

  const onKeyDownRef = useRef(onKeyDown);
  onKeyDownRef.current = onKeyDown;
  /**
   * Готовность принимать правки.
   *
   * Загруженную разметку Quill перестраивает под свою модель (`<div>` → `<p>`, свои пробелы)
   * и сообщает об этом как об обычном изменении. Пока редактор не смонтировался, правки не
   * пользовательские: иначе простое открытие редактора выглядело бы как редактирование.
   */
  const isReadyRef = useRef(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      isReadyRef.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (value === lastEmittedHtmlRef.current) return;
    // Пришедшее значение может отличаться от набранного только способом записи разметки —
    // подставлять такое нельзя: редактор перепишет содержимое и уведёт курсор с места правки
    if (normalizeQuillHtml(editorValueRef.current) === normalizeQuillHtml(value)) return;
    lastEmittedHtmlRef.current = value;
    applyEditorValue(value);
  }, [value, applyEditorValue]);

  /**
   * Клавиши достаются потребителю раньше редактора.
   *
   * Свои сочетания Quill обрабатывает на самом поле, то есть до обработчика на обёртке: Shift+Enter
   * он успевает принять за перенос строки и заменяет им выделенный текст — потребитель сохраняет
   * уже испорченный комментарий. Поэтому слушаем на погружении и, если потребитель сочетание
   * перехватил, до редактора его не пускаем.
   */
  const handleEditorKeyDownCapture = useCallback((event: React.KeyboardEvent) => {
    onKeyDownRef.current?.(event);
    if (event.defaultPrevented) event.stopPropagation();
  }, []);

  const syncFormats = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) setFormats(editor.getFormat() as RichTextFormats);
  }, []);

  const handleChange = useCallback(
    (html: string, _delta: unknown, source: EmitterSource) => {
      // Содержимое запоминаем при любом источнике правки, включая перестройку разметки самим
      // Quill: разойдясь с ним хоть одним пробелом, обёртка начнёт откатывать поле к прошлому
      // значению на каждый повторный рендер — набранный текст при этом пропадает
      applyEditorValue(html);

      // Наружу отдаём только правки пользователя после монтирования: перестройку загруженной
      // разметки Quill сообщает как изменение — и `api`, и `user` (её ведёт наблюдатель за DOM)
      if (source !== 'user' || !isReadyRef.current) return;

      const normalized = normalizeQuillHtml(html);
      lastEmittedHtmlRef.current = normalized;
      onChange(normalized);
      syncFormats();
    },
    [onChange, syncFormats, applyEditorValue],
  );

  useEffect(() => {
    if (autoFocus) quillRef.current?.getEditor().focus();
  }, [autoFocus]);

  useImperativeHandle(
    ref,
    (): RichTextEditorHandle => ({
      focus: () => quillRef.current?.getEditor().focus(),
      getSelectionIndex: () => quillRef.current?.getEditor().getSelection()?.index ?? null,
      getText: (index, length) => quillRef.current?.getEditor().getText(index, length) ?? '',
      replaceText: (index, length, text) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        if (length > 0) editor.deleteText(index, length, 'user');
        editor.insertText(index, text, 'user');
        editor.setSelection(index + text.length, 0, 'silent');
      },
    }),
    [],
  );

  const handleToggleFormat = useCallback(
    (name: 'bold' | 'italic') => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      editor.focus();
      editor.format(name, !editor.getFormat()[name], 'user');
      syncFormats();
    },
    [syncFormats],
  );

  const handleToggleList = useCallback(
    (type: RichTextListType) => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      editor.focus();
      // Повторное нажатие по активному виду списка снимает форматирование
      editor.format('list', editor.getFormat().list === type ? false : type, 'user');
      syncFormats();
    },
    [syncFormats],
  );

  const handleClearFormat = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    editor.focus();
    const selection = editor.getSelection();
    if (!selection) return;
    editor.removeFormat(selection.index, selection.length, 'user');
    syncFormats();
  }, [syncFormats]);

  return (
    <div className={cn('flex flex-col gap-1.5', className)} onKeyDownCapture={handleEditorKeyDownCapture}>
      {!readOnly && (
        <RichTextEditorToolbar
          formats={formats}
          onToggleFormat={handleToggleFormat}
          onToggleList={handleToggleList}
          onClearFormat={handleClearFormat}
        />
      )}
      <ReactQuill
        ref={quillRef}
        theme={EDITOR_THEME}
        modules={EDITOR_MODULES}
        formats={RICH_TEXT_FORMATS}
        value={editorValue}
        onChange={handleChange}
        onChangeSelection={syncFormats}
        placeholder={placeholder}
        readOnly={readOnly}
        className={cn(
          'rounded-md border border-input bg-background text-sm text-foreground',
          'focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50',
          // Сбрасываем оформление Quill по умолчанию (растяжка на всю высоту, свои отступы)
          '[&_.ql-container]:h-auto',
          '[&_.ql-editor]:h-auto [&_.ql-editor]:overflow-y-auto [&_.ql-editor]:p-2',
          '[&_.ql-editor]:leading-relaxed',
          // Плейсхолдер Quill захардкожен чёрным курсивом — приводим к токенам темы
          '[&_.ql-editor.ql-blank::before]:not-italic [&_.ql-editor.ql-blank::before]:left-2',
          '[&_.ql-editor.ql-blank::before]:right-2 [&_.ql-editor.ql-blank::before]:text-muted-foreground',
          editorClassName,
        )}
      />
    </div>
  );
};
